import { gql } from '@apollo/client';
import classNames from 'classnames';
import { inflect } from 'inflection';
import _ from 'lodash';
import React, { useState } from 'react';
import { Link } from '../../utils/router/v3Bridge';
import Badge from '../../components/Badge';
import FormField from '../../components/formInternals/FormField';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import type {
  BillingSummaryStatus,
  PlanInterval,
  PlanRow_BillingCouponFragment,
  PlanRow_BillingTierFragment,
  PlanTier,
} from '../../graphql/generated/graphqlTypes';
import informationIcon from '../../icons/prefilled/information.svg';
import useSeatLimits from '../../invitation/useSeatLimits';
import { GuestsPerSeat } from '../../organization/PricingConfig';
import formatCurrency from '../../utils/formatCurrency';
import styles from './PlanRow.module.scss';
import { getTotalForInterval } from './priceCalculator';

const INTERVAL_TEXT = {
  YEARLY: 'year',
  MONTHLY: 'month',
  QUARTERLY: 'quarter',
};

const getSeatsText = ({
  minimumUserLimit,
  seatCount,
  userLimit,
  amount,
}: {
  minimumUserLimit: number | null;
  seatCount: number;
  userLimit: number | null;
  amount: number | null;
}) => {
  if (minimumUserLimit) {
    return `${minimumUserLimit} seats minimum`;
  }

  // TODO: fix later
  return amount === 0
    ? // @ts-expect-error
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${Math.min(seatCount, userLimit)} of ${userLimit} seats`
    : `${seatCount} ${inflect('seat', seatCount)} × ${formatCurrency(amount)}`;
};

const BILLING_TIER_FRAGMENT = gql`
  fragment PlanRow_billingTier on BillingTier {
    monthly {
      id
      amount
    }
    yearly {
      id
      amount
    }
  }
`;

const BILLING_COUPON_FRAGMENT = gql`
  fragment PlanRow_billingCoupon on BillingCoupon {
    ...getTotalForInterval_billingCoupon
  }
  ${getTotalForInterval.fragments.billingCoupon}
`;

interface PlanRowProps {
  status?: BillingSummaryStatus;
  billingCoupon?: PlanRow_BillingCouponFragment | null;
  interval: PlanInterval;
  onSelect?: () => void;
  billingTier?: PlanRow_BillingTierFragment;
  recommended?: boolean;
  selected?: boolean;
  tooltip: string;
  planTier: PlanTier;
  userLimit?: number | null;
  minimumUserLimit?: number | null;
}

const PlanRow = ({
  status,
  billingCoupon,
  interval,
  onSelect,
  billingTier,
  recommended,
  selected = false,
  tooltip,
  planTier,
  userLimit = null,
  minimumUserLimit = null,
}: PlanRowProps) => {
  const { seatCount } = useSeatLimits({
    guestsPerSeat: GuestsPerSeat[planTier],
  });

  const intervalText = INTERVAL_TEXT[interval];

  const [isHovering, setIsHovering] = useState(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const seatLimitMaxed = _.isNumber(userLimit) && seatCount > userLimit;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const disabledWithTooltip = status === 'TRIALING' || seatLimitMaxed;

  const isDisabled = disabledWithTooltip || !billingTier;
  const amount = billingTier
    ? billingTier[interval.toLowerCase() as 'monthly' | 'yearly']?.amount ??
      null
    : null;

  const totalCost = !_.isNil(amount)
    ? getTotalForInterval(amount, seatCount, billingCoupon)
    : null;

  let disabledTooltip = null;

  if (disabledWithTooltip && isHovering) {
    disabledTooltip = seatLimitMaxed
      ? `Your team must have ${userLimit} users or fewer to downgrade`
      : 'Your team will automatically be moved to Free after your trial ends.';
  }

  const handleHover = (value: boolean) => {
    if (disabledWithTooltip) {
      setIsHovering(value);
    }
  };

  return (
    <label
      className={classNames(styles.row, {
        [styles.active]: selected && !isDisabled,
        [styles.disabled]: isDisabled,
      })}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
    >
      <Tooltip tooltip={disabledTooltip} placement="top">
        <FormField
          type="radio"
          name="tier"
          // @ts-expect-error
          value={planTier}
          margin="none"
          disabled={isDisabled}
          onChange={(e) => {
            if (e.target.checked && onSelect) {
              onSelect();
            }
          }}
          checked={selected}
        />
      </Tooltip>
      <div className={styles.nameColumn}>
        <strong className={styles.name}>{_.capitalize(planTier)}</strong>
        <Tooltip
          tooltip={!disabledTooltip && tooltip}
          trigger="hover"
          placement="top"
          display="block"
        >
          <SVGIcon>{informationIcon}</SVGIcon>
        </Tooltip>
        {recommended && (
          <div className={styles.recommendedBadgeContainer}>
            <Badge color="pink">Recommended</Badge>
          </div>
        )}
      </div>
      <div className={styles.unit}>
        {getSeatsText({
          minimumUserLimit,
          seatCount,
          userLimit,
          amount,
        })}
      </div>
      <div className={styles.price}>
        {billingTier ? (
          <>
            {totalCost}/{intervalText}
          </>
        ) : (
          <Link target="_blank" to="https://slab.com/contact-sales/">
            Contact us
          </Link>
        )}
      </div>
    </label>
  );
};

PlanRow.fragments = {
  billingTier: BILLING_TIER_FRAGMENT,
  billingCoupon: BILLING_COUPON_FRAGMENT,
};

export default PlanRow;
